/**
 * Presence card — bottom-left bubble that expands on click.
 * Minimised by default; collapses again on any outside click.
 * Self-contained: calls usePresence and useTheme internally.
 * Hidden on mobile (< 768px) — presence info lives in the hamburger drawer instead.
 */
import { useState, useEffect, useRef } from 'react';

const HIDE_MOBILE_CSS = `
  @media (max-width: 767px) {
    .presence-corner-card { display: none !important; }
  }
`;
import { useTheme } from '../../../context/ThemeContext.jsx';
import { usePresence } from '../usePresence.js';
import { STATUS_COLORS, ACTIVITY_EMOJI, SOURCE_LABELS } from '../PresenceData.js';

export default function V7CornerCard() {
  const [minimised, setMinimised] = useState(true);
  const cardRef = useRef(null);
  const { darkMode } = useTheme();
  const { presence } = usePresence();

  const { status, activity } = presence;
  const dotColor   = STATUS_COLORS[status] ?? STATUS_COLORS.offline;
  const emoji      = ACTIVITY_EMOJI[activity.type] ?? '❓';
  const sourceText = SOURCE_LABELS[activity.source] ?? '';

  const bgColor     = darkMode ? 'var(--bg-surface-warm)' : 'var(--bg-paper)';
  const textColor   = 'var(--text-heading-warm)';
  const mutedColor  = 'var(--text-muted-warm)';
  const borderColor = 'var(--border-warm)';
  const shadow      = darkMode
    ? '0 4px 20px rgba(0,0,0,0.45)'
    : '0 4px 20px rgba(61,57,41,0.18)';
  const activityBg  = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

  // Collapse on any click outside the card
  useEffect(() => {
    if (minimised) return;
    function handleOutside(e) {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setMinimised(true);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [minimised]);

  /* ── Minimised bubble ─────────────────────────────────────────── */
  if (minimised) {
    return (
      <>
        <style>{HIDE_MOBILE_CSS}</style>
        <button
          ref={cardRef}
          onClick={() => setMinimised(false)}
          aria-label="What's Daulton up to?"
          title="What's Daulton up to?"
          className="presence-corner-card"
          style={{
          position: 'fixed',
          bottom: '1.5rem',
          left: '1.5rem',
          zIndex: 9990,
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: bgColor,
          border: `1.5px solid ${borderColor}`,
          boxShadow: shadow,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '17px',
          lineHeight: 1,
          padding: 0,
        }}
      >
        {emoji}
        <span style={{
          position: 'absolute',
          bottom: '1px',
          right: '1px',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: dotColor,
          border: `2px solid ${bgColor}`,
          boxSizing: 'border-box',
        }} />
      </button>
      </>
    );
  }

  /* ── Expanded card ────────────────────────────────────────────── */
  return (
    <>
      <style>{HIDE_MOBILE_CSS}</style>
      <div
        ref={cardRef}
        className="presence-corner-card"
        style={{
        position: 'fixed',
        bottom: '1.5rem',
        left: '1.5rem',
        zIndex: 9990,
        width: '210px',
        background: bgColor,
        border: `1.5px solid ${borderColor}`,
        borderRadius: '12px',
        boxShadow: shadow,
        overflow: 'hidden',
        padding: '12px',
      }}
    >
      {/* Context label for listening / call */}
      {(activity.type === 'listening' || activity.type === 'call') && (
        <div style={{
          fontFamily: 'var(--font-ui)',
          fontSize: '0.68rem',
          color: mutedColor,
          fontStyle: 'italic',
          marginBottom: '8px',
          opacity: 0.85,
        }}>
          {activity.type === 'listening' ? 'Daulton is listening to:' : 'Daulton is in a call on:'}
        </div>
      )}

      {/* Activity row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {/* Emoji icon block */}
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '8px',
          background: activityBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          lineHeight: 1,
          flexShrink: 0,
        }}>
          {emoji}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: '0.82rem',
            color: textColor,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {activity.name}
          </div>
          <div style={{
            fontFamily: 'var(--font-ui)',
            fontSize: '0.74rem',
            color: mutedColor,
            marginTop: '3px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {activity.detail}
          </div>
          {activity.type !== 'listening' && activity.elapsed && (
            <div style={{
              fontFamily: 'var(--font-ui)',
              fontSize: '0.7rem',
              color: mutedColor,
              marginTop: '3px',
              opacity: 0.75,
            }}>
              for {activity.elapsed}
            </div>
          )}
        </div>
      </div>

      {/* Source badge */}
      <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{
          fontSize: '10px',
          fontFamily: 'var(--font-ui)',
          fontWeight: 600,
          color: mutedColor,
          background: activityBg,
          borderRadius: '4px',
          padding: '2px 7px',
          letterSpacing: '0.03em',
        }}>
          {sourceText}
        </span>
      </div>
    </div>
    </>
  );
}
